import React from "react";
import { Container, Box, Typography } from "@mui/material";
import { animateScroll as scroll } from "react-scroll";
import { useParams } from "react-router-dom";

import { useClientData } from "./hooks/useClientData";
import { useClientEffects } from "./hooks/useClientEffects";

import ClientHeader from "./components/ClientHeader";
import ClientAssignmentStrip from "./components/ClientAssignmentStrip";
import ClientTabs from "./components/ClientTabs";
import ClientFloatingActions from "./components/ClientFloatingActions";
import ClientDialogs from "./components/ClientDialogs";

import "./ClientSummary.css";

const ClientSummary = ({ breadCrumb, onBackClick }) => {
  const { uid, organizationId } = useParams();

  const {
    client,
    protocol,
    activeProtocol,
    dailyReport,
    currentWorkout,
    messageGroup,
    exerciseData,
    nutritionChartData,
    calendarModifiers,
    modifierStyles,
    mealPlans,
    getMealPlanById,
    state,
    setState,
    handlers,
  } = useClientData(uid, organizationId);

  // Custom hook for side effects
  useClientEffects(uid, organizationId, client, setState);

  React.useEffect(() => {
    scroll.scrollToTop({
      smooth: "linear",
      duration: 50,
    });
  }, []);

  if (!organizationId || !client) {
    // If organization data is not available, we should not render the component
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex={1}
        p={4}
      >
        <Typography variant="h6">Loading client information...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <ClientHeader
        client={client}
        breadCrumb={breadCrumb}
        onBackClick={onBackClick}
        onEditClick={() =>
          setState((prev) => ({ ...prev, clientEditOpen: true }))
        }
      />

      <ClientAssignmentStrip
        client={client}
        protocol={protocol}
        activeProtocol={activeProtocol}
        setState={setState}
        getMealPlanById={getMealPlanById}
        coachesArray={handlers.coachesArray}
      />

      <ClientTabs
        activeTab={state.activeTab}
        setActiveTab={(tab) =>
          setState((prev) => ({ ...prev, activeTab: tab }))
        }
        client={client}
        dailyReport={dailyReport}
        currentWorkout={currentWorkout}
        exerciseData={exerciseData}
        calendarModifiers={calendarModifiers}
        modifierStyles={modifierStyles}
        selectedDay={state.selectedDay}
        chartType={state.chartType}
        setChartType={(type) =>
          setState((prev) => ({ ...prev, chartType: type }))
        }
        setSelectedDay={(day) =>
          setState((prev) => ({ ...prev, selectedDay: day }))
        }
        setBodyMeasurementOpen={(open) =>
          setState((prev) => ({ ...prev, bodyMeasurementOpen: open }))
        }
        photoUpdate={state.photoUpdate}
        setPhotoUpdate={(update) =>
          setState((prev) => ({ ...prev, photoUpdate: update }))
        }
        handlers={handlers}
        mealPlans={mealPlans}
        getMealPlanById={getMealPlanById}
        nutritionChartData={nutritionChartData}
        organizationId={organizationId}
      />

      <ClientFloatingActions messageGroup={messageGroup} client={client} />

      <ClientDialogs
        client={client}
        organizationId={organizationId}
        state={state}
        setState={setState}
        handlers={handlers}
      />
    </Container>
  );

};

export default ClientSummary;
